/**
 * @fileoverview Cortex Connection Metrics Service - Phase 4 Compliant
 * @module services/cortex/connection-metrics
 * @description Tracks connection quality, latency, and audio levels for voice conversations.
 *
 * @implements {ICortexConnectionMetrics}
 * @implements {IService}
 * @service-registry
 * **Registry Configuration:**
 * - Key: 'cortexConnectionMetrics'
 * - Factory: `() => cortexConnectionMetrics`
 * - Dependencies: []
 * - Lifecycle: singleton
 *
 * @architecture Phase 4 compliant service
 * @version 1.0.0 - Phase 4 implementation
 * @since 1.0.0 - Modular architecture implementation
 */
import type { ICortexConnectionMetrics, ConnectionMetrics, } from './types';
import type { IService, ServiceResponse } from '../service-registry';
import { logger } from "../../lib/logger";
/**
 * Cortex Connection Metrics Service
 * Tracks and reports connection quality metrics
 */
class CortexConnectionMetricsService implements ICortexConnectionMetrics {
    readonly name = 'cortexConnectionMetrics' as const;
    private currentMetrics: ConnectionMetrics = {
        quality: 'good',
        latency: 0,
        audioInputLevel: 0,
        audioOutputLevel: 0,
        connectionState: 'disconnected',
        timestamp: new Date(),
    };
    private metricsHandlers = new Set<(metrics: ConnectionMetrics) => void>();
    private latencyMeasurements: number[] = [];
    private readonly MAX_LATENCY_SAMPLES = 10;
    /**
     * Initialize the service
     */
    async initialize(): Promise<void> {
        logger.info('🚀 Initializing Cortex Connection Metrics Service...');
        // Service is ready
    }
    /**
     * Dispose the service
     */
    async dispose(): Promise<void> {
        logger.info('🧹 Disposing Cortex Connection Metrics Service...');
        this.metricsHandlers.clear();
        this.latencyMeasurements = [];
    }
    /**
     * Update connection metrics
     */
    async updateMetrics(metrics: Partial<ConnectionMetrics>): Promise<ServiceResponse<void>> {
        try {
            this.currentMetrics = {
                ...this.currentMetrics,
                ...metrics,
                timestamp: new Date(),
            };
            // Calculate quality based on latency
            if (metrics.latency !== undefined) {
                this.latencyMeasurements.push(metrics.latency);
                if (this.latencyMeasurements.length > this.MAX_LATENCY_SAMPLES) {
                    this.latencyMeasurements.shift();
                }
                const avgLatency = this.latencyMeasurements.reduce((a, b) => a + b, 0) /
                    this.latencyMeasurements.length;
                if (avgLatency < 100) {
                    this.currentMetrics.quality = 'excellent';
                }
                else if (avgLatency < 300) {
                    this.currentMetrics.quality = 'good';
                }
                else {
                    this.currentMetrics.quality = 'poor';
                }
            }
            // Emit to all handlers
            this.metricsHandlers.forEach((handler) => {
                try {
                    handler({ ...this.currentMetrics });
                }
                catch (error) {
                    logger.error('Error in metrics handler:', error);
                }
            });
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update metrics';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Get current connection metrics
     */
    async getMetrics(): Promise<ServiceResponse<ConnectionMetrics>> {
        return { success: true, data: { ...this.currentMetrics } };
    }
    /**
     * Subscribe to metrics updates
     */
    onMetricsUpdate(handler: (metrics: ConnectionMetrics) => void): void {
        this.metricsHandlers.add(handler);
    }
    /**
     * Unsubscribe from metrics updates
     */
    offMetricsUpdate(handler: (metrics: ConnectionMetrics) => void): void {
        this.metricsHandlers.delete(handler);
    }
}
// Export singleton instance
export const cortexConnectionMetrics = new CortexConnectionMetricsService();
